import { useCallback, useRef } from "react";
import type { Service } from "../data/services";
import type { NotificationConfig, BudgetLimits } from "../data/environments";

export interface AlertPayload {
  type: "spend_limit" | "renewal_7d" | "renewal_30d";
  subject: string;
  body: string;
}

export function useNotifications(config: NotificationConfig, limits: BudgetLimits) {
  const configRef = useRef(config);
  const limitsRef = useRef(limits);
  useRef(() => { configRef.current = config; });
  useRef(() => { limitsRef.current = limits; });

  const sendAlert = useCallback(async (payload: AlertPayload) => {
    const cfg = configRef.current;
    if (!cfg.enabled || !cfg.smtpEndpoint || !cfg.recipientEmail) return;
    try {
      await fetch(cfg.smtpEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to:      cfg.recipientEmail,
          from:    cfg.senderEmail,
          subject: payload.subject,
          html:    payload.body,
        }),
      });
    } catch (e) {
      console.error("Notification send failed:", e);
    }
  }, []);

  const checkSpendAlerts = useCallback(async (
    services: Service[],
    totalMonthly: number,
    categoryTotals: Record<string, number>,
  ) => {
    const cfg = configRef.current;
    const lim = limitsRef.current;
    if (!cfg.enabled || !cfg.alerts.spendExceedsLimit) return;

    if (lim.totalMonthly > 0 && totalMonthly > lim.totalMonthly) {
      await sendAlert({
        type: "spend_limit",
        subject: `⚠️ W2Cook Billing: Monthly spend limit exceeded ($${totalMonthly.toFixed(2)} / $${lim.totalMonthly})`,
        body: `<h2 style="color:#E8652A">Monthly Spend Alert</h2><p>Total monthly spend of <strong>$${totalMonthly.toFixed(2)}</strong> has exceeded your limit of <strong>$${lim.totalMonthly.toFixed(2)}</strong>.</p><p><a href="${window.location.origin}">View Dashboard →</a></p>`,
      });
    }

    for (const [cat, spent] of Object.entries(categoryTotals)) {
      const limit = lim.perCategory[cat];
      if (limit && limit > 0 && spent > limit) {
        await sendAlert({
          type: "spend_limit",
          subject: `⚠️ W2Cook Billing: ${cat} category limit exceeded`,
          body: `<h2 style="color:#E8652A">Category Alert — ${cat}</h2><p>Spend of <strong>$${spent.toFixed(2)}</strong> exceeded limit of <strong>$${limit.toFixed(2)}</strong>.</p>`,
        });
      }
    }

    for (const svc of services.filter(s => s.status === "active")) {
      const limit = lim.perService[svc.id] ?? svc.budgetLimit;
      if (limit && limit > 0 && svc.monthlyCost > limit) {
        await sendAlert({
          type: "spend_limit",
          subject: `⚠️ W2Cook Billing: ${svc.name} budget exceeded`,
          body: `<h2 style="color:#E8652A">Service Alert — ${svc.name}</h2><p>Cost of <strong>$${svc.monthlyCost.toFixed(2)}</strong> exceeded budget of <strong>$${limit.toFixed(2)}</strong>.</p>`,
        });
      }
    }
  }, [sendAlert]);

  const checkRenewalAlerts = useCallback(async (services: Service[]) => {
    const cfg = configRef.current;
    if (!cfg.enabled) return;
    const today = new Date();

    for (const svc of services.filter(s => s.renewsOn && s.status === "active")) {
      const days = Math.ceil((new Date(svc.renewsOn!).getTime() - today.getTime()) / 86_400_000);

      if (days === 30 && cfg.alerts.renewalWarning30d) {
        await sendAlert({
          type: "renewal_30d",
          subject: `📅 W2Cook Billing: ${svc.name} renews in 30 days`,
          body: `<h2 style="color:#C9A84C">30-Day Renewal Notice</h2><p><strong>${svc.name}</strong> renews on <strong>${svc.renewsOn}</strong>. Cost: $${svc.monthlyCost.toFixed(2)}/mo</p>`,
        });
      }

      if (days === 7 && cfg.alerts.renewalWarning7d) {
        await sendAlert({
          type: "renewal_7d",
          subject: `🔔 W2Cook Billing: ${svc.name} renews in 7 days`,
          body: `<h2 style="color:#E8652A">7-Day Renewal Notice</h2><p><strong>${svc.name}</strong> renews on <strong>${svc.renewsOn}</strong> — only 7 days away. Cost: $${svc.monthlyCost.toFixed(2)}/mo</p>`,
        });
      }
    }
  }, [sendAlert]);

  return { checkSpendAlerts, checkRenewalAlerts, sendAlert };
}

export interface AlertPayload {
  type: "spend_limit" | "renewal_7d" | "renewal_30d";
  subject: string;
  body: string;
}
